import React from 'react'
import { Layout } from '../shared/components'
import { useCssHandles } from '../hooks/useCssHandles'

const CSS_HANDLES = [
  'helpCenter',
  'helpCenterHero',
  'helpCenterHeroContent',
  'helpCenterHeroTitle',
  'helpCenterHeroSubtitle',
  'helpCenterHeroBadge',
  'helpCenterHeroActions',
  'helpCenterHeroButton',
  'helpCenterHeroSecondary',
  'helpCenterSection',
  'helpCenterSectionHeader',
  'helpCenterSectionTitle',
  'helpCenterSectionSubtitle',
  'helpCenterStepsGrid',
  'helpCenterStepCard',
  'helpCenterStepIcon',
  'helpCenterStepTitle',
  'helpCenterStepDescription',
  'helpCenterStepTip',
  'helpCenterTipsGrid',
  'helpCenterTipCard',
  'helpCenterTipTitle',
  'helpCenterTipContent',
  'helpCenterTipExample',
  'helpCenterInfoGrid',
  'helpCenterInfoCard',
  'helpCenterInfoTitle',
  'helpCenterInfoContent',
  'helpCenterAvailability',
  'helpCenterAvailabilityRow',
  'helpCenterAvailabilityLabel',
  'helpCenterAvailabilityValue',
  'helpCenterCTA',
  'helpCenterCTAContent',
  'helpCenterCTATitle',
  'helpCenterCTADescription',
  'helpCenterCTAButton'
] as const

const steps = [
  {
    title: '1. Abra a conversa pelo WhatsApp',
    description: 'Clique em “Falar no WhatsApp” para abrir diretamente nosso atendimento no aplicativo ou no WhatsApp Web.',
    tip: 'Se estiver no computador, confirme a abertura do WhatsApp Web ou escaneie o QR Code. Em smartphones, a conversa abre automaticamente.'
  },
  {
    title: '2. Apresente-se rapidamente',
    description: 'Envie seu nome, cidade e uma frase curta sobre o que você precisa. Isso agiliza o atendimento e permite que a equipe direcione você ao especialista certo.',
    tip: 'Exemplo: “Sou a Ana, de Campinas. Preciso de ajuda para escolher uma bateria para meu Corolla 2018.”'
  },
  {
    title: '3. Compartilhe fotos ou documentos',
    description: 'Se preferir, envie fotos do veículo, manuais ou notas fiscais. Quanto mais informações, mais preciso será o suporte.',
    tip: 'Use o ícone de clipe no WhatsApp para anexar imagens ou PDFs sem sair da conversa.'
  },
  {
    title: '4. Aguarde a confirmação',
    description: 'Nossa equipe responde rapidamente dentro do horário comercial. Enquanto isso, mantenha o WhatsApp aberto para não perder notificações.',
    tip: 'Caso não responda em até 10 minutos, enviaremos um lembrete automático para garantir que você recebeu nossa mensagem.'
  }
]

const quickTopics = [
  {
    title: 'Orçamento imediato',
    content: 'Informe modelo do veículo, ano e cidade para receber um orçamento personalizado em poucos minutos.',
    example: '“Preciso de orçamento para bateria de Honda Civic 2016 em Jaguariúna.”'
  },
  {
    title: 'Status do pedido',
    content: 'Envie o número do pedido ou o CPF utilizado na compra para consultar entrega e instalação.',
    example: '“Pedido #4582 – gostaria de confirmar o agendamento da instalação.”'
  },
  {
    title: 'Suporte técnico',
    content: 'Conte o que está acontecendo com a bateria ou sistema elétrico. Descreva sintomas, luzes no painel ou tentativas anteriores.',
    example: '“Meu painel está oscilando quando ligo o ar-condicionado. Pode ser a bateria?”'
  }
]

const serviceHours = [
  { label: 'Segunda a Sexta', value: '08h – 18h' },
  { label: 'Sábado', value: '08h – 14h' },
  { label: 'Domingo e feriados', value: 'Atendimento sob agendamento' }
]

const whatsappUrl = 'https://wa.me/551932552356?text=Ol%C3%A1!%20Gostaria%20de%20falar%20com%20a%20equipe%20da%20Central%20de%20Ajuda%20da%20Baterpower.'

const HelpCenter: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)

  const handleWhatsAppClick = () => {
    if (typeof window !== 'undefined') {
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <Layout>
      <div className={`${handles.helpCenter} bg-gray-50`}>
        <section className={`${handles.helpCenterHero} relative overflow-hidden`}>
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600" />
          <div className="absolute inset-0 opacity-10">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23FFFFFF\' fill-opacity=\'0.12\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'2\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
              }}
            />
          </div>

          <div className="relative max-w-7xl mx-auto px-6 py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className={`${handles.helpCenterHeroContent} text-white`}>
                <span className={`${handles.helpCenterHeroBadge} inline-flex items-center gap-2 bg-yellow-400 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold shadow-lg mb-6`}>
                  <span className="inline-block w-2 h-2 bg-blue-900 rounded-full animate-pulse" aria-hidden="true" />
                  Atendimento humano pelo WhatsApp
                </span>
                <h1 className={`${handles.helpCenterHeroTitle} text-5xl lg:text-6xl font-bold leading-tight mb-6`}>
                  Central de Ajuda
                  <span className="block text-yellow-300">Fale com especialistas no chat</span>
                </h1>
                <p className={`${handles.helpCenterHeroSubtitle} text-xl text-blue-100 leading-relaxed mb-10`}>
                  Receba orientação personalizada para escolher baterias, acompanhar pedidos e tirar dúvidas em tempo real com nossa equipe pelo WhatsApp.
                </p>

                <div className={`${handles.helpCenterHeroActions} flex flex-col sm:flex-row sm:items-center gap-4`}>
                  <button
                    type="button"
                    onClick={handleWhatsAppClick}
                    className={`${handles.helpCenterHeroButton} inline-flex items-center justify-center px-6 py-3 rounded-xl bg-yellow-400 text-blue-900 font-semibold shadow-lg hover:bg-yellow-300 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-200 focus:ring-offset-blue-900`}
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884" />
                    </svg>
                    Falar no WhatsApp
                  </button>
                  <a
                    href="#orientacoes"
                    className={`${handles.helpCenterHeroSecondary} inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white/10 text-white font-semibold border border-white/20 hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/40 focus:ring-offset-blue-900`}
                  >
                    Ver orientações completas
                  </a>
                </div>
              </div>

              <div className="relative">
                <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-3xl p-8 shadow-2xl">
                  <div className="flex flex-col gap-6">
                    <div className="bg-white/10 rounded-2xl p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-2xl bg-yellow-400 text-blue-900 flex items-center justify-center text-2xl font-bold shadow-lg">
                          1
                        </div>
                        <div>
                          <p className="text-sm text-blue-100 uppercase tracking-wide">Tempo médio de resposta</p>
                          <p className="text-2xl font-semibold text-white">Menos de 5 minutos</p>
                        </div>
                      </div>
                      <p className="text-blue-100 text-sm leading-relaxed">
                        Nossa equipe de especialistas está pronta para responder rapidamente e acompanhar você até a solução completa.
                      </p>
                    </div>

                    <div className="bg-white/10 rounded-2xl p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-2xl bg-green-400 text-blue-900 flex items-center justify-center text-2xl font-bold shadow-lg">
                          2
                        </div>
                        <div>
                          <p className="text-sm text-blue-100 uppercase tracking-wide">Atendentes certificados</p>
                          <p className="text-2xl font-semibold text-white">Equipe técnica própria</p>
                        </div>
                      </div>
                      <p className="text-blue-100 text-sm leading-relaxed">
                        Atendimento humano, sem robôs, com especialistas em sistemas elétricos automotivos e manutenção preventiva.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="hidden lg:block absolute -bottom-8 -left-8 w-40 h-40 bg-yellow-400/20 rounded-full blur-2xl" aria-hidden="true" />
              </div>
            </div>
          </div>
        </section>

        <section id="orientacoes" className={`${handles.helpCenterSection} py-20`}>
          <div className="max-w-7xl mx-auto px-6">
            <div className={`${handles.helpCenterSectionHeader} text-center mb-16`}>
              <h2 className={`${handles.helpCenterSectionTitle} text-4xl font-bold text-gray-900 mb-4`}>
                Como utilizar o atendimento pelo <span className="text-blue-600">WhatsApp</span>
              </h2>
              <p className={`${handles.helpCenterSectionSubtitle} text-lg text-gray-600 max-w-2xl mx-auto`}>
                Siga as orientações abaixo para aproveitar cada conversa com o máximo de eficiência e segurança.
              </p>
            </div>

            <div className={`${handles.helpCenterStepsGrid} grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8`}>
              {steps.map((step) => (
                <article
                  key={step.title}
                  className={`${handles.helpCenterStepCard} bg-white p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className={`${handles.helpCenterStepIcon} w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center text-2xl font-bold shadow-inner`}>
                      {step.title.split('.')[0]}
                    </div>
                    <svg className="w-6 h-6 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20.5a8.5 8.5 0 100-17 8.5 8.5 0 000 17z" />
                    </svg>
                  </div>
                  <h3 className={`${handles.helpCenterStepTitle} text-xl font-semibold text-gray-900 mb-4`}>
                    {step.title}
                  </h3>
                  <p className={`${handles.helpCenterStepDescription} text-gray-600 leading-relaxed mb-4`}>
                    {step.description}
                  </p>
                  <p className={`${handles.helpCenterStepTip} text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-xl p-4 leading-relaxed`}>
                    {step.tip}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={`${handles.helpCenterSection} py-20 bg-white`}>
          <div className="max-w-7xl mx-auto px-6">
            <div className={`${handles.helpCenterSectionHeader} text-center mb-16`}>
              <h2 className={`${handles.helpCenterSectionTitle} text-4xl font-bold text-gray-900 mb-4`}>
                Informações essenciais para o chat
              </h2>
              <p className={`${handles.helpCenterSectionSubtitle} text-lg text-gray-600 max-w-2xl mx-auto`}>
                Compartilhe desde o início os dados abaixo para receber respostas mais rápidas e precisas.
              </p>
            </div>

            <div className={`${handles.helpCenterTipsGrid} grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16`}>
              {quickTopics.map((topic) => (
                <article
                  key={topic.title}
                  className={`${handles.helpCenterTipCard} bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white rounded-3xl p-8 shadow-2xl border border-white/10`}
                >
                  <h3 className={`${handles.helpCenterTipTitle} text-2xl font-semibold mb-4 text-yellow-300`}>
                    {topic.title}
                  </h3>
                  <p className={`${handles.helpCenterTipContent} text-blue-100 leading-relaxed mb-6`}>
                    {topic.content}
                  </p>
                  <div className={`${handles.helpCenterTipExample} bg-white/10 rounded-2xl p-4 text-sm text-blue-50`}>
                    <span className="block text-xs uppercase tracking-widest text-yellow-200 mb-2">
                      Sugestão de mensagem
                    </span>
                    {topic.example}
                  </div>
                </article>
              ))}
            </div>

            <div className={`${handles.helpCenterInfoGrid} grid grid-cols-1 lg:grid-cols-2 gap-8`}>
              <article className={`${handles.helpCenterInfoCard} bg-gray-50 border border-gray-100 rounded-3xl p-8 shadow-inner`}>
                <h3 className={`${handles.helpCenterInfoTitle} text-2xl font-semibold text-gray-900 mb-6`}>
                  Horários de atendimento
                </h3>
                <div className={`${handles.helpCenterAvailability} space-y-4`}>
                  {serviceHours.map((item) => (
                    <div key={item.label} className={`${handles.helpCenterAvailabilityRow} flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3 px-4 rounded-2xl bg-white shadow-sm`}>
                      <span className={`${handles.helpCenterAvailabilityLabel} text-gray-600 font-medium`}>
                        {item.label}
                      </span>
                      <span className={`${handles.helpCenterAvailabilityValue} text-blue-700 font-semibold`}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-sm text-gray-500 leading-relaxed">
                  Mensagens fora do horário recebem resposta priorizada no primeiro horário útil. Para urgências, informe “atendimento urgente” no início do chat.
                </p>
              </article>

              <article className={`${handles.helpCenterInfoCard} bg-white border border-blue-100 rounded-3xl p-8 shadow-lg`}>
                <h3 className={`${handles.helpCenterInfoTitle} text-2xl font-semibold text-gray-900 mb-4`}>
                  Segurança no atendimento
                </h3>
                <ul className={`${handles.helpCenterInfoContent} space-y-4 text-gray-600 leading-relaxed`}>
                  <li className="flex items-start gap-3">
                    <span className="w-2.5 h-2.5 mt-2 rounded-full bg-blue-600" aria-hidden="true" />
                    Confirme que está conversando com o número oficial <strong className="text-gray-900">+55 (19) 3255-2356</strong>.
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2.5 h-2.5 mt-2 rounded-full bg-blue-600" aria-hidden="true" />
                    Nunca solicitamos senhas ou dados de cartão de crédito pelo WhatsApp. Pagamentos são realizados apenas pelos canais oficiais.
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2.5 h-2.5 mt-2 rounded-full bg-blue-600" aria-hidden="true" />
                    Ao finalizar a conversa, você recebe um protocolo de atendimento para acompanhar seu caso quando necessário.
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2.5 h-2.5 mt-2 rounded-full bg-blue-600" aria-hidden="true" />
                    Para compartilhar dados sensíveis, prefira enviar anexos protegidos por senha e informe a senha na mensagem seguinte.
                  </li>
                </ul>
              </article>
            </div>
          </div>
        </section>

        <section className={`${handles.helpCenterCTA} py-20`}>
          <div className="max-w-5xl mx-auto px-6">
            <div className={`${handles.helpCenterCTAContent} relative overflow-hidden rounded-3xl bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 text-slate-900 shadow-2xl`}>
              <div className="absolute -top-24 -right-10 w-64 h-64 bg-white/40 rounded-full blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-20 left-10 w-56 h-56 bg-white/30 rounded-full blur-3xl" aria-hidden="true" />
              <div className="relative px-8 py-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">
                <div>
                  <h2 className={`${handles.helpCenterCTATitle} text-3xl lg:text-4xl font-bold mb-4`}>
                    Pronto para falar com a gente?
                  </h2>
                  <p className={`${handles.helpCenterCTADescription} text-lg text-blue-900/80 leading-relaxed max-w-xl`}>
                    Clique no botão abaixo e inicie o chat com nossos especialistas. Estamos prontos para ajudar você em cada etapa da jornada com a Baterpower.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleWhatsAppClick}
                  className={`${handles.helpCenterCTAButton} inline-flex items-center justify-center px-8 py-4 rounded-2xl bg-slate-900 text-white font-semibold shadow-xl hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 focus:ring-offset-yellow-300`}
                >
                  <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884" />
                  </svg>
                  Abrir conversa no WhatsApp
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default HelpCenter

